<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { FormCreateOrg } from '@/components/forms'
import { Spinner } from '@/components/ui/spinner'
import { ChevronRight, X } from 'lucide-vue-next'

const store = useStore()
const router = useRouter()

const firebaseUser = computed(() => store.getters['user/getUser'])
const member = computed(() => store.getters['user/getMember'])
const organizations = computed(() => store.getters['org/getOrgs'])
const invites = computed(() => store.getters['project/getAllInvites'])
const isLoading = ref(false)

const userAvatar = computed(() => member.value?.avatar_url || firebaseUser.value?.photoURL || null)
const userName = computed(() => member.value?.display_name || firebaseUser.value?.displayName || null)
const userEmail = computed(() => member.value?.email || firebaseUser.value?.email || 'Error')
const userInitial = computed(() => {
  const name = userName.value
  const email = userEmail.value

  if (name) {
    return name.charAt(0).toUpperCase()
  } else if (email) {
    return email.charAt(0).toUpperCase()
  }
  return 'V'
})

const firebaseToken = computed(() => store.getters.getFirebaseToken)

const isFirebaseReady = computed(() => {
  return firebaseUser.value !== null && firebaseToken.value !== null
})

const showCreateOrgForm = ref(false)
const displayOrganizations = computed(() => {
  return organizations.value.map(org => ({
    id: org.organization_id,
    name: org.organization_name,
    avatar: org.avatar_url || '🏢',
    role: org.current_member?.role
  }))
})

const handleCreateOrganization = () => {
  showCreateOrgForm.value = true
}

const handleFormSubmit = async (result) => {
  if (result.success) {
    showCreateOrgForm.value = false

    try {
      await store.dispatch('org/fetchOrgs')

      if (organizations.value.length > 0) {
        const firstOrg = organizations.value[0]
        router.push({ name: 'Organization', params: { org_id: firstOrg.organization_id } })
      } else {
        router.push({ name: 'Dashboard' })
      }
    } catch (error) {
      console.error('Failed to fetch organizations after creation:', error)
      showCreateOrgForm.value = false
    }
  }
}

onMounted(async () => {
  const waitForFirebase = () => {
    return new Promise((resolve) => {
      const checkFirebase = () => {
        if (isFirebaseReady.value) {
          resolve()
        } else {
          setTimeout(checkFirebase, 100)
        }
      }
      checkFirebase()
    })
  }

  isLoading.value = true
  try {
    await waitForFirebase()
    await store.dispatch('org/fetchOrgs')
    await store.dispatch('project/fetchAllInvitesFirebase', { firebaseToken: firebaseToken.value })
  } catch (error) {
    console.error('Failed to fetch data:', error)
  } finally {
    isLoading.value = false
  }
})


const handleCloseForm = () => {
  showCreateOrgForm.value = false
}

const handleJoinProject = async (invite) => {
  await store.dispatch('project/acceptInviteFirebase', {
    invite: invite,
    firebaseToken: firebaseToken.value
  })
}

const handleOpenOrganization = (org) => {
  router.push({ name: 'Organization', params: { org_id: org.id } })
}

const handleLogout = async () => {
  try {
    await store.dispatch('logout')
    router.push({ name: 'AuthLogin' })
  } catch (error) {
    console.error('Logout failed:', error)
  }
}

const handleHome = () => {
  router.push({ name: 'Dashboard' })
}
</script>

<template>
  <div class="p-3 mx-auto">
    <div class="flex justify-between items-center mb-5">
      <div class="flex items-center">
        <Avatar size="base" class="mr-3 bg-primary">
          <AvatarImage v-if="userAvatar" :src="userAvatar" alt="User Avatar" />
          <AvatarFallback class="text-white font-bold">{{ userInitial }}</AvatarFallback>
        </Avatar>
        <div class="text-gray-600 text-sm">
          <div v-if="userName || userEmail">
            <strong v-if="userName" class="text-gray-900">{{ userName }}</strong>
          </div>
          <div>
            <span class="text-gray-500">{{ userEmail }}</span>
            <a href="#" @click.prevent="handleLogout" class="text-blue-600 hover:underline"> Change</a>
          </div>
        </div>
      </div>
    </div>

    <div class="rounded-lg p-6 border border-gray-200 mb-8">
      <h1 class="text-2xl font-bold mb-2">Create a new organization</h1>
      <p class="text-gray-600 mb-4 leading-relaxed">
        Organizations help you manage projects and collaborate with your team. <br>
        Create a new organization to get started with your projects.
      </p>

      <Button @click="handleCreateOrganization">
        Create an Organization
      </Button>
    </div>

    <FormCreateOrg
      :open="showCreateOrgForm"
      @close="handleCloseForm"
      @submit="handleFormSubmit"
    />

    <div v-if="isLoading || !isFirebaseReady" class="flex justify-center py-8">
      <Spinner class="w-6 h-6" />
      <span class="ml-2 text-gray-600">
        {{ !isFirebaseReady ? 'Initializing...' : 'Loading...' }}
      </span>
    </div>

    <div v-else-if="displayOrganizations.length > 0" class="mb-8">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">Available organizations</h2>
      <div class="border border-gray-200 rounded-lg max-h-[300px] overflow-y-auto">
        <div
          v-for="org in displayOrganizations"
          :key="org.id"
          @click="handleOpenOrganization(org)"
          class="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-200 last:border-b-0 transition-colors"
        >
          <div class="flex items-center gap-4">
            <Avatar size="base" class="w-10 h-10 rounded">
              <AvatarImage v-if="org.avatar && org.avatar.startsWith('http')" :src="org.avatar" :alt="org.name" />
              <AvatarFallback class="text-xl">{{ org.avatar || org.name.charAt(0).toUpperCase() }}</AvatarFallback>
            </Avatar>
            <div class="text-sm">
              <h3 class="text-gray-900">{{ org.name }}</h3>
              <p class="text-gray-400">{{ org.role }}</p>
            </div>
          </div>
          <div>
            <Button variant="ghost" size="sm" class="text-gray-500 hover:text-gray-900">
              <span>Open</span>
              <ChevronRight class="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="invites.length > 0" class="mb-8">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">Project invitations</h2>
      <div class="border border-gray-200 rounded-lg max-h-[300px] overflow-y-auto">
        <div
          v-for="invite in invites"
          :key="invite.uuid"
          @click="handleJoinProject(invite)"
          class="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-200 last:border-b-0 transition-colors"
        >
          <div class="flex items-center gap-4">
            <Avatar size="base" class="w-10 h-10 rounded">
              <AvatarFallback class="text-xl">{{ invite.project_name?.charAt(0).toUpperCase() || '📋' }}</AvatarFallback>
            </Avatar>
            <div class="text-sm">
              <h3 class="text-gray-900">{{ invite.project_name }}</h3>
              <p class="text-gray-400">Invited by {{ invite.inviter_name }}</p>
            </div>
          </div>
          <div>
            <Button variant="ghost" size="sm" class="text-gray-500 hover:text-gray-900">
              <span>Join</span>
              <ChevronRight class="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-8 text-center">
      <p class="text-sm text-gray-500 mb-4">
        Not seeing your organization?
        <a href="#" @click.prevent="handleLogout" class="text-blue-600 hover:underline">Try a Different Account</a>
      </p>

      <Button variant="outline" @click="handleLogout" class="px-6 py-2">
        Logout
      </Button>
    </div>
  </div>
</template>
